/**
 * The 849 PARSER config editor for one supplier: the raw config JSON (detection
 * patterns, sheet spec, column mapping), validated against the schema and testable
 * against a stored sample email before saving.
 *
 * Extracted from ConfigAuthoringPage so `/ship-and-debit/suppliers` can render it as
 * a section next to that supplier's other settings. Validation runs before save, so a
 * row cannot land in the state where it fails the schema and the loader silently falls
 * back to the committed file config.
 */
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { T, Var, useGT } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useSampleEmails,
  useSaveSd849Config,
  useTestSd849Config,
  useValidateSd849Config,
} from '@/api/rpc/hooks/ship-and-debit';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { baseSyncDecision } from './editorBaseSync';
import { isLiveParserConfig } from './parserEditorProps';
import type { EditorPanelProps } from './parserEditorProps';
import type { TestSd849ConfigResponse } from '@erp/rpc';

const configObjectSchema = z.record(z.string(), z.unknown());

type ParseResult =
  { ok: true; value: Record<string, unknown> } | { ok: false; error: string };

function parseConfig(text: string): ParseResult {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Invalid JSON',
    };
  }
  // Reject arrays (typeof "object") and non-objects via the schema — no cast.
  const result = configObjectSchema.safeParse(value);
  if (!result.success || Array.isArray(value)) {
    return { ok: false, error: 'Config must be a JSON object.' };
  }
  return { ok: true, value: result.data };
}

function coverageLine(coverage: Record<string, unknown> | null): string {
  if (!coverage) return '—';
  const num = (k: string) =>
    typeof coverage[k] === 'number' ? (coverage[k] as number) : undefined;
  const parts: string[] = [];
  parts.push(
    coverage.parseSupported === true
      ? `${num('total') ?? 0} row(s) [${num('accepted') ?? 0}a/${num('rejected') ?? 0}r/${num('partial') ?? 0}p]`
      : 'parse unsupported',
  );
  const matched = num('matched');
  if (matched !== undefined) {
    parts.push(
      `${matched}m / ${num('ambiguous') ?? 0}amb / ${num('unmatched') ?? 0}unm`,
    );
  }
  return parts.join(' · ');
}

/** The stored config moved under a dirty buffer: saving would revert the winner. */
function ConflictBanner({ onReload }: { onReload: () => void }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs dark:bg-amber-950/30">
      <T>
        The stored config changed while you were editing — the sender addresses
        above write this same config. Saving is disabled because it would revert
        that change. Copy any edits you want to keep, then reload and reapply
        them.
      </T>
      <Button variant="outline" size="sm" onClick={onReload}>
        <T>Reload stored</T>
      </Button>
    </div>
  );
}

/** Schema-validation outcome for the current buffer; nothing until Validate runs. */
function ValidationResult({
  result,
}: {
  result: { valid: boolean; errors: string[] } | null;
}) {
  if (!result) return null;
  if (result.valid) {
    return (
      <div className="rounded-md border border-green-500/40 bg-green-500/10 p-2 text-sm text-green-700 dark:text-green-400">
        <T>Config is valid.</T>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive">
      <div className="font-medium">
        <T>
          <Var>{result.errors.length}</Var> validation error(s):
        </T>
      </div>
      <ul className="ml-4 list-disc">
        {result.errors.slice(0, 20).map((err, i) => (
          <li key={i}>{err}</li>
        ))}
      </ul>
    </div>
  );
}

/** What one dry-run against a stored sample produced. */
function TestOutcome({ result }: { result: TestSd849ConfigResponse }) {
  if (!result.validation.valid) {
    return (
      <div className="text-destructive">
        <T>Config invalid — fix validation errors first.</T>
      </div>
    );
  }
  if (!result.ran) {
    return (
      <div className="text-muted-foreground">
        <T>
          Did not run: <Var>{result.reason}</Var>
        </T>
      </div>
    );
  }
  return (
    <>
      <div className="font-medium">
        <T>
          Coverage: <Var>{coverageLine(result.coverage)}</Var>
        </T>
      </div>
      {result.warnings.length > 0 ? (
        <div>
          <div className="text-muted-foreground">
            <T>Warnings:</T>
          </div>
          <ul className="ml-4 list-disc text-muted-foreground">
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-muted-foreground">
          <T>No warnings.</T>
        </div>
      )}
    </>
  );
}

/** Right-hand column: pick a stored sample and dry-run the buffer against it. */
function SampleTestColumn({
  canRun,
  sampleId,
  onSampleChange,
  isPending,
  onRun,
  result,
}: {
  canRun: boolean;
  sampleId: string | undefined;
  onSampleChange: (id: string) => void;
  isPending: boolean;
  onRun: () => void;
  result: TestSd849ConfigResponse | null;
}) {
  const t = useGT();
  const samples = useSampleEmails({ limit: 100, offset: 0 });
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">
        <T>Test against a sample email</T>
      </span>
      <div className="flex items-center gap-2">
        <Select value={sampleId} onValueChange={onSampleChange}>
          <SelectTrigger className="h-9 flex-1">
            <SelectValue placeholder={t('Pick a sample email…')} />
          </SelectTrigger>
          <SelectContent>
            {(samples.data?.samples ?? []).map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {`[${s.label}] ${(s.subject ?? t('(no subject)')).slice(0, 60)}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          disabled={!canRun || !sampleId || isPending}
          onClick={onRun}
        >
          <T>Test</T>
        </Button>
      </div>

      {result ? (
        <div className="flex flex-col gap-2 rounded-md border p-3 text-sm">
          <TestOutcome result={result} />
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          <T>
            Pick a sample and run the config against it (read-only — nothing is
            written).
          </T>
        </div>
      )}
    </div>
  );
}

/**
 * The loaded row is the one inbound 849 mail is parsed with right now. Saving it
 * is not authoring a draft, so say so before the operator reaches the button.
 */
function LiveConfigBanner() {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs dark:bg-amber-950/30">
      <T context="849 parser config editor, warning above a live config">
        This is the live parser config — it is what inbound 849 emails are read
        with. Saving replaces it immediately; there is no separate activation
        step.
      </T>
    </div>
  );
}

export function ConfigEditorPanel({
  supplierName,
  initialText,
  source,
  status,
  expectedUpdatedAt,
}: EditorPanelProps) {
  const t = useGT();
  const [text, setText] = useState(initialText);
  const [sampleId, setSampleId] = useState<string | undefined>(undefined);
  const [confirmingLiveSave, setConfirmingLiveSave] = useState(false);

  // The stored config can change under this editor: the sender-address field above
  // writes the same row, and its save refetches this query. `key` only remounts on a
  // supplier switch, so without this the textarea would keep pre-refetch JSON and a
  // later save would revert the newer sender patterns. See baseSyncDecision for the
  // three cases — notably, our OWN successful save arrives as a refetch of our own
  // text, which is synchronized rather than a conflict.
  const [syncedInitial, setSyncedInitial] = useState(initialText);
  const [staleBase, setStaleBase] = useState(false);
  if (initialText !== syncedInitial) {
    setSyncedInitial(initialText);
    const decision = baseSyncDecision(text, syncedInitial, initialText);
    if (decision === 'adopt') setText(initialText);
    setStaleBase(decision === 'conflict');
  }

  function reloadStored() {
    setText(initialText);
    setStaleBase(false);
  }

  const validate = useValidateSd849Config();
  const test = useTestSd849Config();
  const save = useSaveSd849Config();

  const parsed = useMemo(() => parseConfig(text), [text]);
  const isLive = isLiveParserConfig(status);

  function runValidate() {
    if (!parsed.ok) return;
    validate.mutate(parsed.value);
  }
  function runTest() {
    if (!parsed.ok || !sampleId) return;
    test.mutate({ sampleId, config: parsed.value });
  }
  function submitSave() {
    // Re-checked, not copied from `runSave`: the confirmation can sit open while
    // the sender-address field writes this same row, so the buffer can go stale
    // between the click that opened the dialog and the one that confirms it.
    //
    // A buffer we ALREADY know is stale must not be savable — that is the cheap,
    // local half. The authoritative half is `expectedUpdatedAt`: the two saves can
    // be in flight together (save addresses, then save draft before the refetch
    // lands), which no client-side flag can see, so the server refuses the write
    // if the row moved. Surface that as the same conflict state.
    if (!parsed.ok || staleBase) return;
    setConfirmingLiveSave(false);
    save.mutate(
      { supplierName, config: parsed.value, expectedUpdatedAt },
      {
        onSuccess: (result) => {
          if (result.conflict) setStaleBase(true);
        },
      },
    );
  }

  /**
   * The button's job when the row is live: ask first. The save itself is
   * unchanged — `submitSave` is the single write path both routes end in, so the
   * confirmation can never drift from what actually gets sent.
   */
  function runSave() {
    if (!parsed.ok || staleBase) return;
    if (isLive) {
      setConfirmingLiveSave(true);
      return;
    }
    submitSave();
  }

  // Technical status chip — computed in JS (not user-facing prose to translate).
  const sourceLabel =
    source === 'authored'
      ? status
        ? `authored · ${status}`
        : 'authored'
      : 'new';

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            <T>Config JSON</T>
          </span>
          <Badge variant="outline">{sourceLabel}</Badge>
          {!parsed.ok ? (
            <span className="text-xs text-destructive">{parsed.error}</span>
          ) : null}
        </div>
        {isLive ? <LiveConfigBanner /> : null}
        {staleBase ? <ConflictBanner onReload={reloadStored} /> : null}
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          className="h-[520px] font-mono text-xs"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!parsed.ok || validate.isPending}
            onClick={runValidate}
          >
            <T>Validate</T>
          </Button>
          <Button
            size="sm"
            disabled={!parsed.ok || staleBase || save.isPending}
            onClick={runSave}
          >
            {isLive ? (
              <T context="849 parser config editor, save button for a live config">
                Save Live Config
              </T>
            ) : (
              <T context="849 parser config editor, save button for a draft config">
                Save Draft
              </T>
            )}
          </Button>
        </div>
        <ValidationResult result={validate.data ?? null} />
        <ConfirmDialog
          open={confirmingLiveSave}
          onClose={() => setConfirmingLiveSave(false)}
          onConfirm={submitSave}
          dangerous
          // The confirm button is an AlertDialogAction, so Radix closes the
          // dialog on click whatever happens next. Without these the dialog can
          // close on a save `submitSave` then silently refuses: the sender-address
          // field writes this same row, so its refetch can turn the buffer stale
          // while the confirmation is open, behind the dialog.
          disabled={!parsed.ok || staleBase}
          loading={save.isPending}
          title={t('Replace the live 849 parser config?', {
            context:
              'confirmation title before saving a live 849 parser config',
          })}
          confirmText={t('Save and Go Live', {
            context: 'confirm button, saves a live 849 parser config',
          })}
          cancelText={t('Cancel', {
            context: 'dismiss the live 849 parser config save confirmation',
          })}
        >
          <p className="text-sm text-muted-foreground">
            <T context="confirmation body before saving a live 849 parser config">
              <Var>{supplierName}</Var>&apos;s config is active, so this save
              takes effect immediately: every 849 email read after it lands is
              parsed with the config in this editor. There is no activation step
              and no deploy to catch a mistake. Version history keeps the
              current config, so it can be rolled back.
            </T>
          </p>
        </ConfirmDialog>
      </div>

      <SampleTestColumn
        canRun={parsed.ok}
        sampleId={sampleId}
        onSampleChange={setSampleId}
        isPending={test.isPending}
        onRun={runTest}
        result={test.data ?? null}
      />
    </div>
  );
}
